import { useHistory, useLocation } from 'react-router-dom';

// string = required param, null = optional (tracked for cleanup, not required for activeDrawer)
type DrawerParamConfig = Record<string, null | Record<string, string | null>>;

type ParamValues<T extends Record<string, string | null>> = {
  [K in keyof T as T[K] extends string ? K : never]: string;
} & {
  [K in keyof T as T[K] extends null ? K : never]?: string;
};

type ActiveDrawer<TConfig extends DrawerParamConfig> = {
  [K in keyof TConfig]: TConfig[K] extends null
    ? { type: K }
    : TConfig[K] extends Record<string, string | null>
    ? { type: K } & ParamValues<TConfig[K]>
    : never;
}[keyof TConfig];

export function useDrawerUrlState<TConfig extends DrawerParamConfig>(
  config: TConfig,
  paramKey = 'drawer',
): {
  activeDrawer: ActiveDrawer<TConfig> | null;
  openDrawer: (drawer: ActiveDrawer<TConfig>) => void;
  closeDrawer: () => void;
} {
  const { search, hash } = useLocation();
  const history = useHistory();

  // Collect all extra param keys across all config entries for cleanup
  const allExtraParamKeys = Object.values(config).flatMap((params) =>
    params ? Object.keys(params) : [],
  );

  const searchParams = new URLSearchParams(search);
  const drawerType = searchParams.get(paramKey) as keyof TConfig | null;

  const activeDrawer = ((): ActiveDrawer<TConfig> | null => {
    if (!drawerType || !(drawerType in config)) return null;

    const extraParams = config[drawerType];

    if (extraParams === null) {
      return { type: drawerType } as ActiveDrawer<TConfig>;
    }

    const extraValues: Record<string, string | undefined> = {};
    for (const key of Object.keys(extraParams)) {
      const value = searchParams.get(key) ?? undefined;
      // Only required (string-defaulted) params must be present
      if (extraParams[key] !== null && !value) return null;
      extraValues[key] = value;
    }

    return { type: drawerType, ...extraValues } as ActiveDrawer<TConfig>;
  })();

  const openDrawer = (drawer: ActiveDrawer<TConfig>) => {
    const next = new URLSearchParams(search);
    for (const key of allExtraParamKeys) {
      next.delete(key);
    }
    next.set(paramKey, drawer.type as string);
    const extraParams = config[drawer.type];
    if (extraParams) {
      for (const key of Object.keys(extraParams)) {
        const value = (drawer as Record<string, string | undefined>)[key];
        if (value !== undefined) {
          next.set(key, value);
        }
      }
    }
    history.replace({ search: next.toString(), hash });
  };

  const closeDrawer = () => {
    const next = new URLSearchParams(search);
    next.delete(paramKey);
    for (const key of allExtraParamKeys) {
      next.delete(key);
    }
    history.replace({ search: next.toString(), hash });
  };

  return { activeDrawer, openDrawer, closeDrawer };
}
